import { useRef, type FormEvent } from "react"

import { Button } from "@coval/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@coval/ui/dialog"
import { FormField } from "@coval/ui/form-field"
import { Input } from "@coval/ui/input"

import { formatTime } from "@/lib/audio-player-utils"

import { parseAudioMarkerTime } from "./audioRegionController"

/**
 * Adds or edits a marker's label and time. Focus starts in the label field and
 * returns to whatever had it before opening.
 */
export function AudioMarkerDialog({
  canSubmit,
  currentTime,
  hoverTime,
  isEditing,
  label,
  manualTime,
  onClose,
  onLabelChange,
  onSubmit,
  onTimeChange,
  open,
}: {
  canSubmit: boolean
  currentTime: number
  hoverTime: number | null
  isEditing: boolean
  label: string
  manualTime: string
  onClose: () => void
  onLabelChange: (label: string) => void
  onSubmit: () => void
  onTimeChange: (time: string) => void
  open: boolean
}) {
  const labelInputRef = useRef<HTMLInputElement>(null)
  const title = isEditing ? "Edit Interruption" : "Add Interruption"
  const timeError =
    manualTime.trim() && parseAudioMarkerTime(manualTime.trim()) === null
      ? "Enter a time as minutes and seconds, like 1:05."
      : undefined
  const submitDisabled = !canSubmit || !label.trim() || Boolean(timeError)

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!submitDisabled) {
      onSubmit()
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose()
        }
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          labelInputRef.current?.focus()
        }}
      >
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <FormField label="Label">
              {(fieldProps) => (
                <Input
                  {...fieldProps}
                  ref={labelInputRef}
                  type="text"
                  value={label}
                  onChange={(event) => onLabelChange(event.target.value)}
                  placeholder="Enter interruption description..."
                />
              )}
            </FormField>
            <FormField label="Time (MM:SS)" error={timeError}>
              {(fieldProps) => (
                <Input
                  {...fieldProps}
                  error={Boolean(timeError)}
                  type="text"
                  value={manualTime}
                  onChange={(event) => onTimeChange(event.target.value)}
                  placeholder="0:00"
                />
              )}
            </FormField>
            <p className="text-sm text-coval-text-muted">
              Current hover time: {formatTime(hoverTime ?? currentTime)}
            </p>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitDisabled}>
              {isEditing ? "Update Interruption" : "Add Interruption"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
